import {
	faAngleLeft,
	faAngleRight,
	faAnglesRight,
	faCalculator,
	faCloudDownloadAlt,
	faCogs,
	faDatabase,
	faExternalLink,
	faPlus,
	faRefresh,
	faSquareRootAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { AavoActionBar } from "src/components/common/actionBar/AavoActionBar.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import type { ControlChartGraphViewRefreshParams } from "src/components/views/spc/controlChart/controlChartGraph/ControlChartGraphView.tsx";
import i18n from "i18next";
import { AavoDateTimePicker } from "src/components/common/inputFields/AavoDateTimePicker.tsx";
import { useState } from "react";
import { Dayjs } from "dayjs";
import { askInputAndOpenControlChartCapabilityView } from "src/components/views/spc/controlChart/utils.tsx";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { AavoMenuButton } from "src/components/common/menu/AavoMenuButton.tsx";
import { actionBarSxContentOnly } from "src/components/common/actionBar/actionBarStyles.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { ControlChartInspectingView } from "src/components/views/spc/controlChart/controlChartInspectingView/ControlChartInspectingView.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { mergeSx } from "src/utils/styles.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { ControlChartDataInputForm } from "src/components/views/spc/controlChart/dataInput/ControlChartDataInputForm.tsx";
import { ControlChartGraphDto } from "src/api/generated/spc/graphs/controlChartGraphDto.ts";
import { Box, Link, Tooltip, Typography } from "@mui/material";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField";
import { ControlChartRecordsDataGrid } from "src/components/views/spc/controlChart/ControlChartRecordsDataGrid.tsx";
import { ControlChartsDataExportForm } from "src/components/views/spc/masterData/controlCharts/ControlChartsDataExportForm.tsx";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { getOnlyMember } from "src/utils/arrayUtils.ts";
import { openControlChartLimitsDialog } from "../analysis/controlChartAnalysisDialogs.tsx";
import { ControlChartSpecialCauseTestsForm } from "src/components/views/spc/masterData/controlCharts/ControlChartSpecialCauseTestsForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ControlChartGraphViewActionBarProps {
	controlCharts: ControlChartGraphDto[];
	refresh: (params?: ControlChartGraphViewRefreshParams) => Promise<unknown>;
	canNavigateBackwards: boolean;
	canNavigateForwards: boolean;
	showOpenInspectingButton?: boolean;
	recordCount: number | null;
	mode?: "full" | "chartMenuOnly";
	showTitle?: boolean;
}

export const ControlChartGraphViewActionBar = ({
	controlCharts,
	refresh,
	canNavigateBackwards,
	canNavigateForwards,
	showOpenInspectingButton = false,
	recordCount,
	mode = "full",
	showTitle = true,
}: ControlChartGraphViewActionBarProps) => {
	const showInputDialog = useInputDialog();
	const { openDialog } = useGenericDialog();

	const [recordsBeforeLimit, setRecordsBeforeLimit] = useState<Dayjs | null>(null);

	const onlyControlChart = getOnlyMember(controlCharts);
	const showFullActionBar = mode === "full";
	const showActionBarTitle = showTitle && onlyControlChart != null;
	const openInspectingView = () => {
		if (onlyControlChart == null) return;
		openDialog({
			title: onlyControlChart.name,
			agentContext: {
				key: defineAgentUiContextKey("spc.controlChart.inspecting.dialog"),
				data: { controlChartId: onlyControlChart.id },
				pathSegments: [onlyControlChart.name],
			},
			content: <ControlChartInspectingView controlChartId={onlyControlChart.id} showControlChartTitle={false} />,
		});
	};
	const openDataInput = () => {
		if (onlyControlChart == null) return;
		openFormOnDialog({
			title: onlyControlChart.name,
			size: "sm",
			component: ControlChartDataInputForm,
			agentContext: {
				key: defineAgentUiContextKey("spc.controlChart.dataInput.formDialog"),
				data: { controlChartId: onlyControlChart.id },
			},
			props: { controlChartId: onlyControlChart.id },
			onSubmit: refresh,
			openDialog,
			confirmCloseIfEdited: false,
			keepOpenAfterSubmit: true,
		});
	};

	return (
		<Box
			sx={{
				alignSelf: "stretch",
				display: "grid",
				gridTemplateColumns: "minmax(0, 1fr) auto",
				gridTemplateAreas:
					showFullActionBar ?
						showActionBarTitle ? '"title menu" "actions actions"'
						:	'"actions menu"'
					:	'"title menu"',
				alignItems: "center",
			}}
		>
			{showActionBarTitle && (
				<TitleText
					controlChart={onlyControlChart}
					showLimitSummary={showFullActionBar}
					onClick={!showFullActionBar && showOpenInspectingButton ? openInspectingView : undefined}
				/>
			)}
			{showFullActionBar && (
				<AavoActionBar sx={{ gridArea: "actions", minWidth: 0, paddingTop: showActionBarTitle ? 0.5 : 1 }}>
					<AsyncButton icon={faRefresh} onClick={() => refresh()} />
					{onlyControlChart && (
						<AsyncButton
							icon={faAngleLeft}
							tooltip={i18n.t("previous_page")}
							disabled={canNavigateBackwards}
							onClick={async () => {
								setRecordsBeforeLimit(null);
								await refresh({ paginationDirection: "BACKWARD" });
							}}
						/>
					)}
					{onlyControlChart && (
						<AsyncButton
							icon={faAngleRight}
							tooltip={i18n.t("next_page")}
							disabled={canNavigateForwards}
							onClick={async () => {
								setRecordsBeforeLimit(null);
								await refresh({ paginationDirection: "FORWARD" });
							}}
						/>
					)}
					{onlyControlChart && (
						<AsyncButton
							icon={faAnglesRight}
							tooltip={i18n.t("latest_data")}
							onClick={async () => {
								setRecordsBeforeLimit(null);
								await refresh({ paginationDirection: "LAST_PAGE" });
							}}
						/>
					)}
					<AavoDateTimePicker
						label={i18n.t("time_limit")}
						value={recordsBeforeLimit}
						onAccept={async (value: Dayjs | null) => {
							setRecordsBeforeLimit(value);
							await refresh({ recordsBeforeLimit: value, paginationDirection: "CURRENT_PAGE" });
						}}
					/>
					{onlyControlChart && showOpenInspectingButton && (
						<AsyncButton
							label={i18n.t("open")}
							icon={faExternalLink}
							variant={"outlined"}
							onClick={openInspectingView}
						/>
					)}
					{onlyControlChart != null && !onlyControlChart.timeBasedMeasurements && (
						<AsyncButton
							label={i18n.t("input_data")}
							icon={faPlus}
							variant={"outlined"}
							onClick={openDataInput}
						/>
					)}
				</AavoActionBar>
			)}
			<HorizontalBox
				sx={mergeSx(actionBarSxContentOnly, {
					gridArea: "menu",
					paddingY: 1,
					minWidth: "unset",
					ml: "auto",
					justifyContent: "flex-end",
				})}
			>
				<AavoMenuButton
					menuContent={(closeMenu) => [
						!showFullActionBar && onlyControlChart && showOpenInspectingButton && (
							<AsyncButton
								key={"open"}
								label={i18n.t("open")}
								icon={faExternalLink}
								variant={"menu"}
								onClick={() => {
									openInspectingView();
									closeMenu();
								}}
							/>
						),
						!showFullActionBar && onlyControlChart && !onlyControlChart.timeBasedMeasurements && (
							<AsyncButton
								key={"inputData"}
								label={i18n.t("input_data")}
								icon={faPlus}
								variant={"menu"}
								onClick={() => {
									openDataInput();
									closeMenu();
								}}
							/>
						),
						onlyControlChart && (
							<AsyncButton
								key={"capability"}
								label={i18n.t("capability")}
								icon={faSquareRootAlt}
								variant={"menu"}
								onClick={async () => {
									await askInputAndOpenControlChartCapabilityView({
										showInputDialog,
										openDialog,
										controlChartId: onlyControlChart.id,
										chartName: onlyControlChart.name,
									});
									closeMenu();
								}}
							/>
						),
						onlyControlChart && (
							<AsyncButton
								key={"data"}
								label={i18n.t("data.spc")}
								icon={faDatabase}
								variant={"menu"}
								onClick={() => {
									openDialog({
										title: onlyControlChart.name,
										agentContext: {
											key: defineAgentUiContextKey("spc.controlChart.records.dialog"),
											data: { controlChartId: onlyControlChart.id },
											pathSegments: [i18n.t("data.spc")],
										},
										content: <ControlChartRecordsDataGrid controlChartId={onlyControlChart.id} />,
									});
									closeMenu();
								}}
							/>
						),
						<AsyncMenuButton
							key={"exportDataButton"}
							icon={faCloudDownloadAlt}
							label={i18n.t("export_data")}
							onClick={() => {
								openFormOnDialog({
									openDialog,
									title: i18n.t("export_data"),
									size: "sm",
									component: ControlChartsDataExportForm,
									agentContext: {
										key: defineAgentUiContextKey("spc.controlCharts.dataExport.formDialog"),
										data: { controlChartIds: controlCharts.map((chart) => chart.id) },
									},
									props: {
										controlChartIds: controlCharts.map((c) => c.id),
									},
									confirmCloseIfEdited: false,
								});
							}}
						/>,
						onlyControlChart && (
							<AsyncMenuButton
								key={"controlLimitsButton"}
								icon={faCalculator}
								label={i18n.t("control_limits")}
								onClick={() => {
									openControlChartLimitsDialog({
										openDialog,
										controlChartId: onlyControlChart.id,
									});
								}}
							/>
						),
						onlyControlChart && (
							<AsyncMenuButton
								key={"selectSpecialCauseTestButton"}
								icon={faCogs}
								label={i18n.t("select_special_cause_tests")}
								onClick={async () => {
									openDialog(({ closeDialog }) => ({
										size: "md",
										title: i18n.t("select_special_cause_tests"),
										agentContext: {
											key: defineAgentUiContextKey("spc.controlChart.specialCauseTests.dialog"),
											data: { controlChartId: onlyControlChart.id },
											pathSegments: [i18n.t("select_special_cause_tests")],
										},
										content: (
											<ControlChartSpecialCauseTestsForm
												controlChartId={onlyControlChart.id}
												onCompleted={async () => {
													await closeDialog();
												}}
											/>
										),
									}));
								}}
							/>
						),
						<AavoTextField
							key={"dataPointCount"}
							label={i18n.t("data_point_count")}
							type={"number"}
							defaultValue={recordCount}
							onSubmit={async (value) => {
								const asNum = Number(value);
								const valueToUse =
									isNaN(asNum) ? 10
									: asNum > 300 ? 300
									: asNum < 0 ? 0
									: asNum;
								await refresh({ recordCount: valueToUse });
							}}
							sx={{
								marginX: 1,
								marginTop: 1,
							}}
						/>,
					]}
				/>
			</HorizontalBox>
		</Box>
	);
};

interface TitleTextProps {
	controlChart: ControlChartGraphDto;
	showLimitSummary: boolean;
	onClick?: () => void;
}

const TitleText = ({ controlChart, showLimitSummary, onClick }: TitleTextProps) => {
	const { currentLimits, lsl, usl, target } = controlChart;

	const parts = [
		{
			label: "X̄",
			value: currentLimits?.xAvg,
		},
		{
			label: i18n.t("lcl"),
			value: currentLimits?.xLcl,
		},
		{
			label: i18n.t("ucl"),
			value: currentLimits?.xUcl,
		},
		{
			label: i18n.t("target.spc"),
			value: target,
		},
		{
			label: i18n.t("lsl"),
			value: lsl,
		},
		{
			label: i18n.t("usl"),
			value: usl,
		},
	];
	const limitSummary = parts
		.filter((part) => part.value != null)
		.map((part) => `${part.label}: ${part.value}`)
		.join(", ");
	const heading = showLimitSummary ? `${controlChart.name} (${limitSummary})` : controlChart.name;
	const title =
		onClick == null ?
			<Typography variant="h6" noWrap tabIndex={0} aria-label={heading}>
				{heading}
			</Typography>
		:	<Link
				component="button"
				type="button"
				variant="h6"
				underline="hover"
				noWrap
				aria-label={heading}
				onClick={onClick}
				sx={{ minWidth: 0, textAlign: "left" }}
			>
				{heading}
			</Link>;

	return (
		<HorizontalBox sx={{ gridArea: "title", alignItems: "center", minWidth: 0, paddingX: 1, paddingTop: 0.5 }}>
			<Tooltip
				title={
					<Box>
						<Box>{heading}</Box>
						{controlChart.description && <Box sx={{ marginTop: 0.5 }}>{controlChart.description}</Box>}
					</Box>
				}
			>
				{title}
			</Tooltip>
		</HorizontalBox>
	);
};
